import { Modal, Box, Typography, FormControl, TextField, Button, useTheme } from "@mui/material";
import React, { useContext } from "react";
import { Asset, AssetByAddressResponseAddressResult, DocumentAIAnalysis } from "../../../api";
import { assetsApi, inspectionsApi } from "../../../api-interface";
import { TenantContext } from "../../..";
import { useNavigate } from "react-router-dom";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { enqueueSnackbar } from "notistack";


const Page2AssetFound = ({ asset, decrementPage, analysis_id }: { asset: Asset, decrementPage: () => void, analysis_id: string }) => {
    const navigate = useNavigate()
    return <><Typography variant="h6"><TaskAltIcon fontSize="inherit"/> Asset found - Is this the correct asset?</Typography>
        <Box sx={{ml: 2}}>
            <Typography>{asset.address_1}</Typography>
            <Typography>{String(asset.address_2)}</Typography>
            <Typography>{asset.city}</Typography>
            <Typography>{asset.county}</Typography>
            <Typography>{asset.postcode}</Typography>
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" gap={1} textAlign={"right"}>
            <Button variant="outlined" onClick={() => {
                decrementPage()
            }}>No</Button>
            <Button variant="contained" onClick={async () => {
                try{
                    await inspectionsApi(asset.tenant_id).setAnalysisAssetIdInspectionsAnalysisIdPost(analysis_id, asset.id)
                    navigate(0)
                } catch (e) {
                    enqueueSnackbar("Error: " + e, { variant: "error" })
                }
            }}>Yes</Button>

        </Box>
    </>
}

const Page2AssetNotFound = ({ decrementPage, newAddress, analysis_id, placeFound }: { decrementPage: () => void, newAddress: object, analysis_id: string, placeFound: AssetByAddressResponseAddressResult  }) => {
    const tenant_id = useContext(TenantContext)
    const navigate = useNavigate()
    return <><Typography variant="h6"><ErrorOutlineIcon fontSize='inherit' /> Asset not found. Would you like to create an asset at this address?</Typography>
        <Box sx={{ml: 2}}>
            <Typography>{placeFound.address_1}</Typography>
            {placeFound.address_2 && <Typography>{String(placeFound.address_2)}</Typography>}
            <Typography>{placeFound.city}</Typography>
            <Typography>{placeFound.county}</Typography>
            <Typography>{placeFound.postcode}</Typography>
            <Typography><b>UPRN: </b>{placeFound.UPRN}</Typography>
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" gap={1} textAlign={"right"}>
        <Button variant="outlined" onClick={() => {
                decrementPage()
            }}>No</Button>
            <Button variant="contained" onClick={async () => {
                try{
                    await assetsApi(tenant_id).createAssetAndAssociateAnalysisAssetsPost(newAddress["address_1"], newAddress["address_2"], newAddress["city"], newAddress["county"], newAddress["postcode"], analysis_id)
                    navigate(0)
                } catch (e) {
                    enqueueSnackbar("Error: " + e, { variant: "error" })
                } 

            }}>Yes</Button>

        </Box>
    </>
}

const Page1 = ({ newAddress, setNewAddress, incrementPage, setPlaceFound, setAsset, placeFound }: { newAddress: object, setNewAddress: (address: object) => void, incrementPage: () => void, setAsset: (asset: Asset) => void, setPlaceFound: (found: AssetByAddressResponseAddressResult) => void, placeFound: AssetByAddressResponseAddressResult | null }) => {
    const tenant_id = useContext(TenantContext)

    const search = async (address_1, address_2, town, county, postcode) => {
        try{
            let asset_response = await assetsApi(tenant_id).getAssetByAddressAssetsByAddressGet(address_1, address_2, town, county, postcode)
            setAsset(asset_response.data.asset)
            setPlaceFound(asset_response.data.address_result)
            if (asset_response.data.address_result) {
                incrementPage()
            }
        } catch (e) {
            enqueueSnackbar("Error: " + e, { variant: "error" })
        }
    }
    const [notFound, setNotFound] = React.useState(false)
    const theme = useTheme()
    return <>
        <Typography id="modal-modal-title" variant="h6" component="h2">
            Fix Asset Association
        </Typography>
        {notFound && <Typography color={theme.palette.error.main}>Could not locate this address. Please update the fields and try again</Typography>}
        <FormControl fullWidth>
            <TextField
                margin="normal"
                label="Address Line 1"
                value={newAddress["address_1"]}
                onChange={(e) => setNewAddress({ ...newAddress, "address_1": e.target.value })}
                type="text"
                variant='outlined'
                required
            />
            <TextField
                margin="normal"
                label="Address Line 2"
                value={newAddress["address_2"]}
                onChange={(e) => setNewAddress({ ...newAddress, "address_2": e.target.value })}
                type="text"
                variant='outlined'
                required
            />
            <TextField
                margin="normal"
                label="Town/City"
                value={newAddress["city"]}
                onChange={(e) => setNewAddress({ ...newAddress, "city": e.target.value })}
                type="text"
                variant='outlined'
                required
            />
            <TextField
                margin="normal"
                label="County"
                value={newAddress["county"]}
                onChange={(e) => setNewAddress({ ...newAddress, "county": e.target.value })}
                type="text"
                variant='outlined'
                required
            />
            <TextField
                margin="normal"
                label="Postcode"
                value={newAddress["postcode"]}
                onChange={(e) => setNewAddress({ ...newAddress, "postcode": e.target.value })}
                type="text"
                variant='outlined'
                required
            />
            <Box textAlign={"right"}>
                <Button variant="contained" onClick={async () => {
                    await search(newAddress["address_1"], newAddress["address_2"], newAddress["city"], newAddress["county"], newAddress["postcode"])
                    if (!placeFound) {
                        setNotFound(true)
                    }
                }}>Search</Button>
            </Box>
        </FormControl>

    </>
}

const AddressCorrectionModal = ({ open, handleClose, analysis }: { open: boolean, handleClose: () => void, analysis: DocumentAIAnalysis }) => {
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        minWidth: 100,
        p: 4,
    };
    const [page, setPage] = React.useState(1)
    const [asset, setAsset] = React.useState<Asset | null>(null)
    const [newAddress, setNewAddress] = React.useState({
        "address_1": analysis.site_address_line_1,
        "address_2": analysis.site_address_line_2,
        "city": analysis.site_address_town_city,
        "county": analysis.site_address_county,
        "postcode": analysis.site_address_postcode
    })
    const [placeFound, setPlaceFound] = React.useState<AssetByAddressResponseAddressResult | null>(null)
    const incrementPage = () => setPage(page + 1)
    const decrementPage = () => setPage(page - 1)
    return <Modal
        open={open}
        onClose={() => {
            setPage(1)
            setAsset(null)
            setNewAddress({
                "address_1": analysis.site_address_line_1,
                "address_2": analysis.site_address_line_2,
                "city": analysis.site_address_town_city,
                "county": analysis.site_address_county,
                "postcode": analysis.site_address_postcode
            })
            handleClose()
        }}
    >
        <Box sx={style}>
            {page === 1 && <Page1 placeFound={placeFound} newAddress={newAddress} setNewAddress={setNewAddress} setAsset={setAsset} setPlaceFound={setPlaceFound} incrementPage={incrementPage}></Page1>}
            {(page === 2 && asset) && <Page2AssetFound analysis_id={analysis.id} asset={asset!!} decrementPage={decrementPage}></Page2AssetFound>}
            {(page === 2 && !asset) && <Page2AssetNotFound analysis_id={analysis.id} placeFound={placeFound!}  newAddress={newAddress} decrementPage={decrementPage}></Page2AssetNotFound>}
        </Box>
    </Modal>
}
export default AddressCorrectionModal